import { Compass, Flame, Feather } from "lucide-react";
import { site } from "@/data/siteContent";

const MANDALA_URL =
  "https://images.unsplash.com/photo-1729335511904-9b8690184935?auto=format&fit=crop&w=1200&q=80";

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="section relative"
    >
      <div className="container-luxe grid lg:grid-cols-[0.85fr_1.15fr] gap-16 items-center">
        {/* Left: Vedic Yantra / Mandala illustration (no portrait) */}
        <div className="reveal relative">
          <div className="relative rounded-2xl overflow-hidden aspect-[4/5] gold-border glass-strong">
            {/* Background zodiac mandala */}
            <div
              aria-hidden
              className="absolute inset-0 animate-slow-spin"
              style={{
                backgroundImage: `url(${MANDALA_URL})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter:
                  "sepia(0.4) hue-rotate(-8deg) saturate(1.2) brightness(0.75)",
                opacity: 0.65,
              }}
            />
            {/* Dark scrim to make emblem readable */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(5,11,20,0.4)_0%,rgba(5,11,20,0.85)_75%)]" />

            {/* Inner gold frame with Om */}
            <div className="absolute inset-6 border border-[#D4AF37]/30 rounded-xl grid place-items-center text-center px-6">
              <div>
                <div
                  className="font-devanagari text-gold text-7xl leading-none"
                  style={{
                    textShadow:
                      "0 0 40px rgba(212,175,55,0.55), 0 0 12px rgba(212,175,55,0.4)",
                  }}
                >
                  ॐ
                </div>
                <div className="mt-5 font-display text-[10.5px] tracking-[0.4em] text-gold">
                  VEDIC · JYOTISH
                </div>
                <div className="mt-3 h-px w-20 mx-auto bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" />
                <p className="mt-5 font-editorial italic text-cream-muted text-[13.5px] leading-relaxed">
                  “Ancient wisdom, <br /> quiet guidance.”
                </p>
                <p className="mt-4 font-devanagari text-cream-muted text-sm">
                  प्राचीन ज्ञान, आधुनिक मार्गदर्शन
                </p>
              </div>
            </div>
          </div>

          <div className="absolute -bottom-8 -right-6 glass px-6 py-5 rounded-2xl max-w-[220px]">
            <div className="font-display text-3xl gold-text">25+</div>
            <div className="mt-1 text-[11px] tracking-[0.22em] uppercase text-cream-muted">
              Years devoted to Vedic sciences
            </div>
          </div>
        </div>

        {/* Right copy */}
        <div className="reveal">
          <div className="text-[11px] tracking-[0.4em] uppercase text-gold-light">
            About · परिचय
          </div>
          <h2
            data-testid="about-title"
            className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] text-cream"
          >
            A quiet guide, an{" "}
            <span className="italic font-editorial gold-text">ancient</span>{" "}
            science.
          </h2>
          <p className="mt-6 text-cream-muted leading-relaxed max-w-2xl">
            {site.brand.name} was born from a lineage of temple scholars where
            Sanskrit shlokas were the first lullabies. Over two and a half
            decades, this practice has translated the classical wisdom of
            Parashari astrology into practical clarity for seekers across the
            world — students, families, entrepreneurs and spiritual aspirants
            alike.
          </p>
          <p className="mt-4 text-cream-muted leading-relaxed max-w-2xl">
            The work stands on three quiet principles:{" "}
            <em>truth over comfort</em>, <em>compassion over judgement</em>, and{" "}
            <em>remedy over prediction</em>. Every reading is an invitation to
            step, however gently, in the direction the stars are already
            pointing.
          </p>

          <div className="mt-10 grid sm:grid-cols-3 gap-5">
            {[
              {
                icon: Flame,
                title: "Authentic",
                desc: "Rooted in classical Vedic texts.",
              },
              {
                icon: Feather,
                title: "Personal",
                desc: "Every reading is one-of-one.",
              },
              {
                icon: Compass,
                title: "Practical",
                desc: "Remedies you can live daily.",
              },
            ].map((f) => (
              <div
                key={f.title}
                className="glass rounded-xl p-5"
                data-testid={`about-value-${f.title.toLowerCase()}`}
              >
                <f.icon className="w-6 h-6 text-gold" strokeWidth={1.4} />
                <div className="mt-4 font-display text-sm tracking-[0.22em] text-cream">
                  {f.title.toUpperCase()}
                </div>
                <div className="mt-2 text-[13px] text-cream-muted leading-relaxed">
                  {f.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
